import type { LucideIcon } from "lucide-react";
import {
  AlertTriangle,
  Award,
  Box,
  Brush,
  Car,
  Code2,
  BookOpenText,
  Crosshair,
  FileCode2,
  FileDiff,
  FolderOpen,
  Gauge,
  Image as ImageIcon,
  ImageDown,
  Layers,
  Package,
  Palette,
  ScanSearch,
  Scissors,
  Shirt,
  Sparkles,
  UserPlus,
  Wand2,
  Workflow,
  Zap,
} from "lucide-react";
import type { FeatureKey } from "@/lib/authz/policy";
import { FREE_TOOLS, type FreeToolKey } from "@/components/freeTools/FreeToolsNav";
import type { HubBadgeKind } from "./HubBadge";

export type HubCategoryId = "create" | "design" | "optimize" | "code";

export type HubAccessTag =
  | "credits"
  | "monthly-credits"
  | "open-access"
  | "standard-plus"
  | "pro-free"
  | "tier-rates"
  | "premium-library";

export type HubTool = {
  title: string;
  blurb: string;
  href: string;
  icon: LucideIcon;
  badge?: "new" | "ai" | "beta";
  /** Feature gate. Tools without one are always accessible. */
  featureKey?: FeatureKey;
  /** Detail panel enrichment */
  formatTag?: string;
  accessTags?: HubAccessTag[];
  planValue?: string;
  bullets?: string[];
  bestValue?: string;
  fileTags?: string[];
};

export type HubCategory = {
  id: HubCategoryId;
  label: string;
  hint: string;
  tools: HubTool[];
};

export const HUB_CATEGORIES: HubCategory[] = [
  {
    id: "create",
    label: "Create",
    hint: "Generate new assets from scratch.",
    tools: [
      {
        title: "Model Forge",
        blurb: "Generate game-ready 3D models from a prompt or reference.",
        href: "/model-forge",
        icon: Sparkles,
        badge: "ai",
        featureKey: "tripoai",
        fileTags: [".glb", ".obj"],
        accessTags: ["credits", "monthly-credits"],
        planValue: "3D model generation is credit-priced. Standard, Pro, and Max plans include monthly credits so you can generate without topping up as often.",
        bullets: [
          "Generate from text prompt or reference image",
          "Exports game-ready GLB models",
          "AI-enhanced prompt refinement via Gemini",
        ],
        bestValue: "Standard",
      },
      {
        title: "Asset Art",
        blurb: "Produce inventory icons and item artwork with guided AI.",
        href: "/item-image",
        icon: ImageIcon,
        badge: "ai",
        featureKey: "itemimage",
        fileTags: [".png", ".webp"],
        accessTags: ["credits", "monthly-credits"],
        planValue: "Image generation is credit-priced. Paid plans include monthly credits for steady asset production.",
        bullets: [
          "Guided prompting for consistent item art",
          "Inventory-ready output sizes",
          "Batch-friendly for large item sets",
        ],
        bestValue: "Standard",
      },
      {
        title: "Badge Maker",
        blurb: "Generate badges, crests, and emblems for liveries and crews.",
        href: "/badge-maker",
        icon: Award,
        badge: "ai",
        featureKey: "badgemaker",
        fileTags: [".png", ".webp"],
        accessTags: ["credits", "monthly-credits"],
        planValue: "Badge generation runs on the same credit pool as Asset Art. Monthly credits apply here too.",
        bullets: [
          "MC logos, crew patches, and gang emblems",
          "Vector-style output for clean livery use",
          "Shares monthly credits with Asset Art",
        ],
        bestValue: "Standard",
      },
      {
        title: "Embroidery Tool",
        blurb: "Upload artwork and convert it into an embroidered patch.",
        href: "/embroidery-tool",
        icon: Scissors,
        badge: "ai",
        featureKey: "embroiderytool",
        fileTags: [".png", ".webp", ".jpg"],
        accessTags: ["credits", "monthly-credits"],
        planValue: "Embroidery conversion uses the same credit pool and access as Badge Maker.",
        bullets: [
          "Upload logos, badges, icons, or patch art",
          "AI converts artwork into stitched thread texture",
          "Saves outputs to a dedicated embroidery history",
        ],
        bestValue: "Standard",
      },
      {
        title: "Add-on EUP Creator",
        blurb: "Scaffold a streaming-ready EUP add-on resource.",
        href: "/addon-eup-creator",
        icon: UserPlus,
        featureKey: "addoneupcreator",
        fileTags: [".ydd", ".ytd", ".lua", ".zip"],
        accessTags: ["standard-plus"],
        planValue: "Included with Standard and above. No extra credit cost — generate as many resources as you need.",
        bullets: [
          "Scaffolds a complete streaming resource",
          "Auto-generates fxmanifest.lua",
          "Drop-in ready for your FiveM server",
        ],
        bestValue: "Standard",
      },
      {
        title: "Easy Meta",
        blurb: "Generate vehicles.meta, handling, carcols and friends.",
        href: "/easy-meta",
        icon: FileCode2,
        featureKey: "metaroom",
        fileTags: [".meta", ".xml"],
        formatTag: "META",
        accessTags: ["standard-plus"],
        planValue: "Included with Standard and above.",
        bullets: [
          "vehicles.meta, handling.meta, carcols.meta",
          "Template library for common vehicle types",
          "Export-ready XML output",
        ],
        bestValue: "Standard",
      },
      {
        title: "Handling Diff",
        blurb: "Compare two handling.meta files field by field.",
        href: "/handling-diff",
        icon: FileDiff,
        featureKey: "handlingdiff",
        fileTags: [".meta"],
        accessTags: ["open-access"],
        planValue: "Included with Vehicle Developer, Pro, and Studio plans.",
        bullets: [
          "Field-by-field handling.meta comparison",
          "Threshold-aware — ignores float noise, never a real change",
          "Flags unrecognised fields the game silently ignores",
        ],
        bestValue: "Standard",
      },
    ],
  },
  {
    id: "design",
    label: "Design",
    hint: "Visually edit textures, liveries, and assets.",
    tools: [
      {
        title: "Creator Convert",
        blurb: "Convert DDS textures, create DDS files, or extract YTDs.",
        href: "/creator-convert",
        icon: ImageDown,
        badge: "new",
        featureKey: "creatorconvert",
        fileTags: [".dds", ".ytd", ".zip", ".png", ".webp", ".jpg"],
        accessTags: ["standard-plus"],
        planValue: "Included with Standard and above. Runs locally in the browser with no credit cost.",
        bullets: [
          "DDS to PNG, WebP, or JPEG",
          "Image to DXT, BC, or 32-bit RGBA DDS",
          "YTD texture extraction to ZIP",
        ],
        bestValue: "Standard",
      },
      {
        title: "Paint Booth Studio",
        blurb: "Photoshop-style canvas docked to a live 3D vehicle preview.",
        href: "/paintbooth-studio",
        icon: Brush,
        featureKey: "paintbooth",
        fileTags: [".psd", ".pdn", ".xcf", ".png"],
        accessTags: ["standard-plus"],
        planValue: "Included with Standard and above.",
        bullets: [
          "Photoshop-style layers on a live 3D preview",
          "Export-ready DDS and PNG textures",
          "Works with custom model imports",
        ],
        bestValue: "Standard",
      },
      {
        title: "Paint Booth",
        blurb: "Live livery preview on 3D vehicles.",
        href: "/paintbooth",
        icon: Palette,
        featureKey: "paintbooth",
        fileTags: [".psd", ".pdn", ".xcf", ".png"],
        accessTags: ["pro-free"],
        planValue: "Available on all paid plans including the free tier.",
        bullets: [
          "Drag-and-drop DDS/PNG livery preview",
          "Supports hundreds of vehicle models",
          "No export required — live preview only",
        ],
        bestValue: "Pro Free",
      },
      {
        title: "UV Studio",
        blurb: "Recover and inspect UV templates from mapped models.",
        href: "/uv-studio",
        icon: Layers,
        featureKey: "uvstudio",
        fileTags: [".yft", ".obj", ".png"],
        accessTags: ["open-access"],
        planValue: "Open to all subscribers — no credits required.",
        bullets: [
          "Recover UV templates from any mapped model",
          "Export as PNG or SVG for Photoshop",
          "Supports FiveM YFT and OBJ formats",
        ],
        bestValue: "Standard",
      },
      {
        title: "EUP Viewer",
        blurb: "Inspect EUP outfits before they reach the server.",
        href: "/eup-viewer",
        icon: Shirt,
        featureKey: "eupviewer",
        fileTags: [".ydd", ".ytd"],
        accessTags: ["open-access"],
        planValue: "Open to all subscribers.",
        bullets: [
          "Preview EUP packs before streaming",
          "Filter by component type and variant",
          "Drag-in your own resource folder",
        ],
        bestValue: "Standard",
      },
      {
        title: "Weapon Designer",
        blurb: "Iterate on weapon textures without an in-game reload loop.",
        href: "/weapon-viewer",
        icon: Crosshair,
        featureKey: "weaponviewer",
        fileTags: [".ydr", ".ytd", ".png"],
        accessTags: ["standard-plus"],
        planValue: "Included with Standard and above.",
        bullets: [
          "Live 3D weapon preview with texture swap",
          "No in-game reload loop needed",
          "Export your final DDS directly",
        ],
        bestValue: "Standard",
      },
      {
        title: "Blink Lab",
        blurb: "Build siren and lightbar patterns on an 8-color grid.",
        href: "/blinklab",
        icon: Zap,
        featureKey: "blinklab",
        formatTag: "YFT · META",
        fileTags: [".xml", ".dat", ".json"],
        accessTags: ["standard-plus"],
        planValue: "Included with Standard and above.",
        bullets: [
          "8-color siren grid editor",
          "Real-time pattern preview",
          "Export carcols.meta-ready sequences",
        ],
        bestValue: "Standard",
      },
      {
        title: "Chevron Builder",
        blurb: "Generate rear warning chevron patterns for emergency vehicles.",
        href: "/chevron-builder",
        icon: AlertTriangle,
        badge: "new",
        featureKey: "chevronbuilder",
        fileTags: [".png"],
        accessTags: ["open-access"],
        planValue: "Open to all subscribers.",
        bullets: [
          "Custom colors, angles, and stripe ratios",
          "Mirror mode, honeycomb overlay, rotation",
          "Export print-ready PNG at any resolution",
        ],
        bestValue: "Standard",
      },
    ],
  },
  {
    id: "optimize",
    label: "Optimize",
    hint: "Compress, package, and ship your work.",
    tools: [
      {
        title: "PackBench",
        blurb: "Bundle models, textures, and meta into a clean resource zip.",
        href: "/pack-bench",
        icon: Package,
        featureKey: "packbench",
        fileTags: [".yft", ".ytd", ".lua", ".zip"],
        accessTags: ["open-access"],
        planValue: "Open to all subscribers — no credits required.",
        bullets: [
          "Bundle models, textures, and meta into a zip",
          "Validates resource structure before export",
          "One-click deployment-ready output",
        ],
        bestValue: "Standard",
      },
    ],
  },
  {
    id: "code",
    label: "Code",
    hint: "Author and audit FiveM scripts.",
    tools: [
      {
        title: "Code Smith",
        blurb: "Scan resources for performance, security, manifest, and asset risks.",
        href: "/code-smith",
        icon: ScanSearch,
        featureKey: "codevisualizer",
        fileTags: [".lua", ".js", ".zip", ".pdf"],
        accessTags: ["standard-plus"],
        planValue: "Included with Standard and above.",
        bullets: [
          "Scans exposed secrets, unsafe events, and obfuscation",
          "Detects loop abuse, missing waits, and manifest risks",
          "Per-file report with severity ratings",
        ],
        bestValue: "Standard",
      },
      {
        title: "Code Visualizer",
        blurb: "Map FiveM scripts into readable blocks for events, commands, exports, and functions.",
        href: "/code-visualizer",
        icon: Workflow,
        badge: "new",
        featureKey: "codevisualizer",
        fileTags: [".lua", ".js", ".ts", ".json", ".cfg", ".xml", ".meta", ".zip"],
        accessTags: ["standard-plus"],
        planValue: "Included anywhere Code Visualizer is available.",
        bullets: [
          "Turns script structure into grouped visual blocks",
          "Finds commands, events, exports, callbacks, and threads",
          "Links triggers to matching registered events",
        ],
        bestValue: "Standard",
      },
      {
        title: "Script Docs",
        blurb: "Generate README-ready docs from resource scripts and manifests.",
        href: "/script-docs",
        icon: BookOpenText,
        badge: "new",
        featureKey: "scriptdocs",
        fileTags: [".lua", ".js", ".ts", ".json", ".cfg", ".xml", ".meta", ".zip", ".md"],
        accessTags: ["standard-plus"],
        planValue: "Included with Server Developer, Pro, and Studio plans.",
        bullets: [
          "Summarizes commands, events, exports, and dependencies",
          "Finds likely config files and manifest metadata",
          "Exports clean Markdown documentation",
        ],
        bestValue: "Standard",
      },
      {
        title: "Resmon Report",
        blurb: "Turn a resmon or txAdmin dump into a ranked performance report.",
        href: "/resmon",
        icon: Gauge,
        featureKey: "resmon",
        fileTags: [".txt", ".csv", ".json"],
        accessTags: ["open-access"],
        planValue: "Included with Server Developer, Pro, and Studio plans.",
        bullets: [
          "Ranks resources by per-frame CPU cost",
          "Reconciles against the dump's declared total",
          "Surfaces unrecognised lines instead of hiding them",
        ],
        bestValue: "Standard",
      },
    ],
  },
];

export type HubLibraryLink = {
  title: string;
  href: string;
  icon: LucideIcon;
};

export const HUB_LIBRARY: HubLibraryLink[] = [
  { title: "My Projects", href: "/my-projects", icon: FolderOpen },
  { title: "Account", href: "/account", icon: Box },
  { title: "Guides", href: "/guides", icon: FileCode2 },
];

export type HubAssetTag = "all" | "vehicles" | "props" | "clothing" | "weapons" | "scripts";

export const HUB_ASSET_TAGS: { id: HubAssetTag; label: string }[] = [
  { id: "all", label: "All" },
  { id: "vehicles", label: "Vehicles" },
  { id: "clothing", label: "Clothing" },
  { id: "props", label: "Props" },
  { id: "weapons", label: "Weapons" },
  { id: "scripts", label: "Scripts" },
];

/**
 * Owner-curated tool order within an asset category, by href. Where a category
 * appears here, its tools render in exactly this sequence on every asset-grouped
 * surface — the subscriber home grid (CreatorHome), the marketing tools grid
 * (LandingToolsGrid), and the hub's asset discovery — taking precedence over the
 * admin sort_order and declaration order. Categories left out keep their default
 * sort; an href absent from a listed category sorts after the listed ones.
 *
 * This is the single place to re-sequence Vehicles / Clothing across the product.
 * The "just shipped" float still applies to a new tool that hasn't been placed
 * in its category's list yet, so freshly-shipped tools still surface up front
 * until they're woven into the curated order here.
 */
export const ASSET_CATEGORY_TOOL_ORDER: Partial<Record<HubAssetTag, string[]>> = {
  vehicles: [
    "/paintbooth",
    "/uv-studio",
    "/blinklab",
    "/chevron-builder",
    "/paintbooth-studio",
    "/easy-meta",
    "/creator-convert",
  ],
  clothing: [
    "/eup-viewer",
    "/embroidery-tool",
    "/badge-maker",
    "/addon-eup-creator",
  ],
};

/**
 * Rank of an href within its asset category's curated order, or -1 when the
 * category isn't curated or the href isn't listed. Lower ranks sort first.
 */
export function assetCategoryToolRank(tag: HubAssetTag, href: string): number {
  const order = ASSET_CATEGORY_TOOL_ORDER[tag];
  return order ? order.indexOf(href) : -1;
}

export type HubAssetTile = {
  id: string;
  title: string;
  byline: string;
  href: string;
  icon: LucideIcon;
  tags: HubAssetTag[];
  badge?: HubBadgeKind;
};

/**
 * Curated launchers for the right pane. These are real destinations inside
 * the Workshop until we wire a public asset feed — the user can replace
 * them with live data later by swapping out HUB_ASSET_TILES.
 */
export const HUB_ASSET_TILES: HubAssetTile[] = [
  {
    id: "modelforge",
    title: "Generate a 3D prop",
    byline: "Model Forge",
    href: "/model-forge",
    icon: Sparkles,
    tags: ["all", "props"],
    badge: "ai",
  },
  {
    id: "paintbooth-studio",
    title: "Paint a vehicle livery",
    byline: "Paint Booth Studio",
    href: "/paintbooth-studio",
    icon: Brush,
    tags: ["all", "vehicles"],
  },
  {
    id: "paintbooth",
    title: "Preview a livery in 3D",
    byline: "Paint Booth",
    href: "/paintbooth",
    icon: Palette,
    tags: ["all", "vehicles"],
  },
  {
    id: "badge-maker",
    title: "Design a crew badge",
    byline: "Badge Maker",
    href: "/badge-maker",
    icon: Award,
    tags: ["all", "clothing"],
    badge: "ai",
  },
  {
    id: "embroidery-tool",
    title: "Embroider uploaded art",
    byline: "Embroidery Tool",
    href: "/embroidery-tool",
    icon: Scissors,
    tags: ["all", "clothing"],
    badge: "ai",
  },
  {
    id: "easy-meta",
    title: "Generate vehicle meta files",
    byline: "Easy Meta",
    href: "/easy-meta",
    icon: FileCode2,
    tags: ["all", "vehicles"],
  },
  {
    id: "pack-bench",
    title: "Package a FiveM resource",
    byline: "PackBench",
    href: "/pack-bench",
    icon: Package,
    tags: ["all", "scripts"],
  },
  {
    id: "weapon-viewer",
    title: "Iterate on weapon textures",
    byline: "Weapon Designer",
    href: "/weapon-viewer",
    icon: Crosshair,
    tags: ["all", "weapons"],
  },
  {
    id: "eup-viewer",
    title: "Browse EUP outfits",
    byline: "EUP Viewer",
    href: "/eup-viewer",
    icon: Shirt,
    tags: ["all", "clothing"],
  },
  {
    id: "addon-eup",
    title: "Build an EUP add-on pack",
    byline: "Add-on EUP Creator",
    href: "/addon-eup-creator",
    icon: UserPlus,
    tags: ["all", "clothing"],
  },
  {
    id: "code-smith",
    title: "Scan a FiveM resource",
    byline: "Code Smith",
    href: "/code-smith",
    icon: ScanSearch,
    tags: ["all", "scripts"],
  },
  {
    id: "code-visualizer",
    title: "Map a script into blocks",
    byline: "Code Visualizer",
    href: "/code-visualizer",
    icon: Workflow,
    tags: ["all", "scripts"],
    badge: "new",
  },
  {
    id: "blinklab",
    title: "Edit a siren pattern",
    byline: "Blink Lab",
    href: "/blinklab",
    icon: Zap,
    tags: ["all", "vehicles"],
  },
  {
    id: "chevron-builder",
    title: "Build a rear warning chevron",
    byline: "Chevron Builder",
    href: "/chevron-builder",
    icon: AlertTriangle,
    tags: ["all", "vehicles"],
    badge: "new",
  },
  {
    id: "creator-convert",
    title: "Convert creator textures",
    byline: "Creator Convert",
    href: "/creator-convert",
    icon: ImageDown,
    tags: ["all", "vehicles"],
    badge: "new",
  },
  {
    id: "asset-art",
    title: "Generate an inventory icon",
    byline: "Asset Art",
    href: "/item-image",
    icon: ImageIcon,
    tags: ["all", "props"],
    badge: "ai",
  },
  {
    id: "script-docs",
    title: "Document a script resource",
    byline: "Script Docs",
    href: "/script-docs",
    icon: BookOpenText,
    tags: ["all", "scripts"],
  },
  {
    id: "uv",
    title: "Recover a UV map",
    byline: "UV Studio",
    href: "/uv-studio",
    icon: Layers,
    tags: ["all", "vehicles"],
  },
  {
    id: "handling-diff",
    title: "Compare two handling.meta files",
    byline: "Handling Diff",
    href: "/handling-diff",
    icon: FileDiff,
    tags: ["all", "vehicles"],
    badge: "new",
  },
  {
    id: "resmon",
    title: "Rank a resmon dump",
    byline: "Resmon Report",
    href: "/resmon",
    icon: Gauge,
    tags: ["all", "scripts"],
    badge: "new",
  },
];

/**
 * Which asset group each free `/free/**` tool sits in on the subscriber home.
 * Typed against `FreeToolKey`, so adding a free tool fails typecheck here until
 * it is placed.
 */
const FREE_TOOL_ASSET_TAG: Record<FreeToolKey, Exclude<HubAssetTag, "all">> = {
  joaat: "scripts",
  vector3: "scripts",
  "server-cfg": "scripts",
  "spawn-index": "vehicles",
  "clash-finder": "scripts",
  "stream-budget": "scripts",
  "spawn-rename": "vehicles",
  "id-bench": "vehicles",
};

/**
 * The free tools, shaped as ordinary hub tools so a subscriber sees them in
 * the same grid as everything else. Deliberately silent: no `badge`, no
 * `featureKey` (always unlocked), and not in NEW_FEATURE_TOOLS, so they never
 * float to the front or wear a recency marker. Kept out of HUB_CATEGORIES so the
 * free-user home, the hub welcome and the showcase tool picker stay as they are.
 */
export const HUB_FREE_TOOLS: (HubTool & { assetTag: Exclude<HubAssetTag, "all"> })[] = FREE_TOOLS.map(
  (tool) => ({
    title: tool.label,
    blurb: tool.blurb,
    href: tool.href,
    icon: tool.icon,
    assetTag: FREE_TOOL_ASSET_TAG[tool.key],
  }),
);

/** Every tool a subscriber can launch: the paid catalogue, then the free tools. */
export const SUBSCRIBER_HUB_TOOLS: HubTool[] = [
  ...HUB_CATEGORIES.flatMap((category) => category.tools),
  ...HUB_FREE_TOOLS,
];

export const HUB_VEHICLE_FALLBACK: LucideIcon = Car;
export const HUB_CODE_FALLBACK: LucideIcon = Code2;
export const HUB_CROSSHAIR: LucideIcon = Crosshair;
